import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import Joi from "joi-browser";
import moment from "moment";
import Input from "./Input";
import {
  formatDate,
  createEntity,
  updateEntity,
  getFieldName,
  getFieldDate,
} from "../../scripts/helperScripts";
import Select from "./Select";

function FRCPSModal({
  isOpen,
  hideModal,
  nextModal,
  isLastModal,
  clientId,
  clientProgram,
  initialData,
  updateClientIdentifiers,
}) {
  const clientProgramAdmitDate = new Date(
    clientProgram && getFieldDate(clientProgram.ccx_admitdate)
  );
  const [clientIdentifier, setClientIdentifier] = useState({});
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  const joiSchema = {
    ccx_clientidentifiersid: Joi.string(),
    ccx_name: Joi.string().required().label("Identifier"),
    ccx_effdate: Joi.date()
      .required()
      .max(
        moment(clientProgramAdmitDate).isAfter(moment())
          ? moment(clientProgramAdmitDate).toDate()
          : "now"
      )
      .min(new Date("1/1/1900"))
      .label("Effective Date")
      .error((errors) => {
        errors.forEach((error) => {
          switch (error.type) {
            case "date.max":
              error.message = `Effective Date cannot be future dated beyond ${moment(
                error.context.limit
              ).format("L")}`;
              break;
            case "date.min":
              error.message = `Effective Date cannot be before 1/1/1900`;
              break;
            default:
              return error.message;
          }
        });
        return errors;
      }),
    ccx_expdate: Joi.alternatives([
      Joi.date()
        .max(
          new Date(clientIdentifier.ccx_effdate) > new Date()
            ? new Date(clientIdentifier.ccx_effdate)
            : "now"
        )
        .min(new Date(clientIdentifier.ccx_effdate))
        .allow(null)
        .label("Expiration Date")
        .error((errors) => {
          errors.forEach((error) => {
            switch (error.type) {
              case "date.min":
                error.message = `Expiration Date needs to be after Effective Date of ${moment
                  .utc(error.context.limit)
                  .format("L")}`;
                break;
              case "date.max":
                error.message = `Expiration Date cannot be future dated beyond Effective Date of ${moment
                  .utc(error.context.limit)
                  .format("L")}`;
                break;
              default:
                return error.message;
            }
          });
          return errors;
        }),
      Joi.string().valid(""),
    ]),
  };

  useEffect(() => {
    if (!safyInfo) navigate("/");
  });

  useEffect(() => {
    if (initialData) setClientIdentifier(initialData);
  }, [initialData]);

  const handleChange = ({ currentTarget: input }) => {
    const errs = { ...errors };
    const errorMessage = validateProperty(input);

    if (errorMessage) {
      errs[input.name] = errorMessage;
    } else {
      delete errs[input.name];
    }
    const data = { ...clientIdentifier };
    data[input.name] = input.value;
    setClientIdentifier(data);

    setErrors(errs);
  };

  const validateProperty = ({ name, value }) => {
    const obj = { [name]: value };
    const schema = { [name]: joiSchema[name] };
    const { error } = Joi.validate(obj, schema);
    return error ? error.details[0].message : null;
  };

  const validate = () => {
    const options = { abortEarly: false };
    const { error } = Joi.validate(clientIdentifier, joiSchema, options);

    let validationErrors = {};
    if (error)
      for (let item of error.details)
        validationErrors[item.path[0]] = item.message;

    if (Object.keys(validationErrors).length === 0) return null;

    setErrors(validationErrors);
    return validationErrors;
  };

  const handleSave = () => {
    const obj = {
      ccx_client: {
        type: "entity",
        value: {
          entityName: "contact",
          entityId: clientId,
        },
      },
      ccx_name: {
        type: "string",
        value: clientIdentifier.ccx_name,
      },
      ccx_clientidentifiertype: {
        type: "optionset",
        value: 186460006, //FRCPS
      },
      ccx_effdate: {
        type: "datetime",
        value: new Date(clientIdentifier.ccx_effdate),
      },
      ccx_expdate: {
        type: "datetime",
        value:
          clientIdentifier.ccx_expdate !== undefined ||
          clientIdentifier.ccx_expdate !== ""
            ? new Date(clientIdentifier.ccx_expdate)
            : null,
      },
    };

    if (clientIdentifier.ccx_clientidentifiersid) {
      const toastId = toast.loading("FRCPS is being updated...", {
        type: toast.TYPE.INFO,
      });
      updateEntity(
        "ccx_clientidentifiers",
        clientIdentifier.ccx_clientidentifiersid,
        JSON.stringify(obj),
        safyInfo
      )
        .then(() => {
          toast.update(toastId, {
            render: "FRCPS has been updated.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
          updateClientIdentifiers && updateClientIdentifiers();
        })
        .catch(() => {
          toast.update(toastId, {
            render: "An error occurred while updating FRCPS.",
            type: toast.TYPE.ERROR,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
        });
    } else {
      const toastId = toast.loading("FRCPS is being created...", {
        type: toast.TYPE.INFO,
      });
      createEntity(
        "ccx_clientidentifiers",
        JSON.stringify(obj),
        safyInfo,
        getFieldName(clientProgram.ccx_company).split(" of ")[1]
      )
        .then(() => {
          updateClientIdentifiers && updateClientIdentifiers();
          toast.update(toastId, {
            render: "FRCPS has been created.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
        })
        .catch(() => {
          toast.update(toastId, {
            render: "An error occurred while creating FRCPS.",
            type: toast.TYPE.ERROR,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
        });
    }
  };

  const resetForm = () => {
    setClientIdentifier({
      ccx_name: "",
      ccx_effdate: formatDate(new Date()),
    });
    setErrors({});
  };

  const handleHideModal = () => {
    if (hideModal) {
      resetForm();
      hideModal();
    }
  };

  const options = [
    { _id: "Kinship", name: "Kinship" },
    { _id: "Family Preservation", name: "Family Preservation" },
    { _id: "Investigation", name: "Investigation" },
  ];

  return (
    <>
      {isOpen ? (
        <Modal show={isOpen} animation={false} size="sm">
          <Modal.Header>
            <Modal.Title>FRCPS</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Select
              name="ccx_name"
              label="Identifier:"
              value={clientIdentifier.ccx_name}
              onChange={handleChange}
              options={options}
              error={errors.ccx_name}
            />
            <Input
              name="ccx_effdate"
              label="Effective Date"
              type="date"
              value={clientIdentifier.ccx_effdate}
              onChange={handleChange}
              error={errors.ccx_effdate}
            />
            {clientIdentifier.ccx_clientidentifiersid && (
              <Input
                name="ccx_expdate"
                label="Expiration Date"
                type="date"
                value={clientIdentifier.ccx_expdate}
                onChange={handleChange}
                error={errors.ccx_expdate}
              />
            )}
          </Modal.Body>
          <Modal.Footer>
            {nextModal === undefined && (
              <>
                <button className="btn btn-danger" onClick={handleHideModal}>
                  Cancel
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) {
                      handleSave();
                      handleHideModal();
                    }
                  }}
                >
                  Submit
                </button>
              </>
            )}
            {nextModal && (
              <>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    resetForm();
                    nextModal();
                  }}
                >
                  Skip
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) {
                      handleSave();
                      resetForm();
                      nextModal();
                    }
                  }}
                >
                  Submit and {isLastModal ? "End" : "Next"}
                </button>
              </>
            )}
          </Modal.Footer>
        </Modal>
      ) : null}
    </>
  );
}

export default FRCPSModal;
